import { useCallback, useState } from 'react';
import {
  isErpTaskSubmissionResponse,
  isTaskPollingCancelledError,
  postErpAsyncTask,
  waitForErpTasks
} from '@/lib/task-api';
import { useTaskPollingSignal } from '@/hooks/use-task-polling-signal';
import { ExcelConfig, type ImportBody } from '../utils/excel-util';
import type { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';

interface UsePhieuXuatKhoImportProps {
  closeImportDialog: () => void;
  refreshData: () => Promise<void>;
  transactionOptions: { value: string; label: string }[];
}

export const usePhieuXuatKhoImport = ({
  closeImportDialog,
  refreshData,
  transactionOptions
}: UsePhieuXuatKhoImportProps) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const { entityUnit, entity } = useAuth();
  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();
  const getTaskPollingSignal = useTaskPollingSignal();

  const handleImportBodies = useCallback(
    async (bodies: ImportBody[]) => {
      const BATCH_SIZE = 120;
      let totalCreated = 0;
      let totalFailed = 0;
      let hasError = false;

      try {
        for (let index = 0; index < bodies.length; index += BATCH_SIZE) {
          const batch = bodies.slice(index, index + BATCH_SIZE);
          let data: any;

          try {
            const response = await postErpAsyncTask<any>(
              `/entities/${entity?.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_KHO}/bulk-create/`,
              {
                list: batch
              }
            );
            data = response.data;
          } catch (error) {
            console.error('Bulk create error:', error);
            totalFailed += batch.length;
            hasError = true;
            throw error;
          }

          if (isErpTaskSubmissionResponse(data)) {
            if (!entity?.slug) {
              throw new Error('Không tìm thấy thông tin công ty để kiểm tra tác vụ nhập Excel.');
            }
            const [taskResult] = await waitForErpTasks<{ success?: boolean; created?: number; failed?: number }>({
              entitySlug: entity.slug,
              tasks: [data],
              intervalMs: 2000,
              signal: getTaskPollingSignal()
            });
            const result = taskResult.result;

            if (result && typeof result === 'object' && 'success' in result) {
              totalCreated += result.created ?? 0;
              totalFailed += result.failed ?? 0;
              if (!result.success) hasError = true;
            } else {
              totalCreated += 1;
            }
            continue;
          }

          if (data && typeof data === 'object' && 'success' in data) {
            totalCreated += data.created ?? 0;
            totalFailed += data.failed ?? 0;
            if (!data.success) hasError = true;
          } else {
            totalCreated += Array.isArray(data) ? data.length : 0;
          }
        }

        if (hasError && totalCreated === 0) {
          toast({
            description: 'Không thể nhập phiếu xuất kho. Vui lòng kiểm tra dữ liệu và thử lại.',
            variant: 'destructive'
          });
        } else {
          toast({
            description:
              totalFailed === 0
                ? 'Đã nhập phiếu xuất kho từ Excel thành công'
                : 'Đã nhập phiếu xuất kho từ Excel, một số chứng từ có lỗi',
            variant: 'default'
          });
        }
      } catch (error) {
        if (isTaskPollingCancelledError(error)) return;
        console.error('Import error:', error);
        toast({
          description: error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập phiếu xuất kho từ Excel.',
          variant: 'destructive'
        });
        throw error;
      } finally {
        await refreshData();
      }
    },
    [entity?.slug, getTaskPollingSignal, refreshData]
  );

  const handleImportFromExcel = useCallback(
    async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
      setIsImporting(true);

      if (!entityUnit?.uuid) {
        toast({
          description: 'Không tìm thấy thông tin đơn vị',
          variant: 'destructive'
        });
        setIsImporting(false);
        return;
      }

      if (!data.file) {
        toast({
          description: 'Vui lòng chọn file Excel',
          variant: 'destructive'
        });
        setIsImporting(false);
        return;
      }

      try {
        const maxErrors = parseInt(data.errors || '200', 10);
        const sheetIndex = parseInt(data.sheetIndex || '0', 10);

        const result = await processFile({
          file: data.file,
          sheetIndex,
          maxErrors,
          getUUIDByCode,
          context: {
            entityUnitUUID: entityUnit.uuid,
            defaultMaGD: transactionOptions[0]?.value || 'NB'
          }
        });

        if (result.success && result.data) {
          await handleImportBodies(result.data);
          closeImportDialog();
          return;
        }

        if (result.errors && result.columnMapping) {
          setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
          setShowErrorDialog(true);
          closeImportDialog();
        }
      } finally {
        setIsImporting(false);
      }
    },
    [closeImportDialog, entityUnit?.uuid, getUUIDByCode, handleImportBodies, processFile, transactionOptions]
  );

  const handleImportErrorClose = useCallback(() => {
    setShowErrorDialog(false);
    setErrorData(null);
  }, []);

  return {
    isImporting,
    importErrorDialog: {
      open: showErrorDialog,
      errorData,
      onClose: handleImportErrorClose
    },
    handleImportFromExcel
  };
};
